import React from "react";
import Text from "./Text";
import { useTranslation } from "react-i18next";

type Variant = "start" | "goal"; // start=現在地, goal=目的地

type StartGoalSpeechBubbleProps = {
  variant: Variant;
  /** ラベルを上書きしたい場合に指定。未指定時はデフォルト（start: 現在地 / goal: 目的地） */
  label?: string;
  className?: string;
};

const COLOR_CLASSES: Record<Variant, { borderColor: string; arrowColor: string }> = {
  start: { borderColor: "border-AccentBlue", arrowColor: "after:border-t-AccentBlue" },
  goal: { borderColor: "border-MiraiPrimary", arrowColor: "after:border-t-MiraiPrimary" },
};

/**
 * StartGoalSpeechBubble
 * Figmaの「現在地」「目的地」チップを1つのコンポーネントで表現。
 * - Textコンポーネントでタイポ（BIZ UDPGothic, 16px, line-height: 2）を再現
 * - 枠線は2px相当（視認性のためpx指定を維持）
 * - 吹き出しの三角はCSSボーダーで実装（サイズはpxのまま）
 */
const StartGoalSpeechBubble: React.FC<StartGoalSpeechBubbleProps> = ({
  variant,
  label,
  className = "",
}) => {
  // 翻訳インスタンスのインポート
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language ?? "ja";
  const isEnglish = language.startsWith("en");

  const { borderColor, arrowColor } = COLOR_CLASSES[variant];
  const text = label ?? (variant === "start" ? t("現在地") : t("目的地"));

  return (
    <div className={["flex", "flex-col", "items-center", className].join(" ")}>
      {/* バルーン本体（擬似要素で下三角を生成） */}
      <div
        className={[
          "relative",
          "bg-white",
          "box-border",
          "flex",
          "items-center",
          "justify-center",
          "px-3",
          "rounded",
          "border-solid",
          "border-[2px]",
          borderColor,
          // 下三角（バルーンの下端に常に接する）
          "after:content-['']",
          "after:absolute",
          "after:left-1/2",
          "after:-translate-x-1/2",
          "after:border-l-[6px]",
          "after:border-r-[6px]",
          "after:border-t-[8px]",
          "after:border-l-transparent",
          "after:border-r-transparent",
          "after:bottom-[-8px]",
          arrowColor,
        ].join(" ")}
      >
        <Text as="span" variant={isEnglish ? "body/s" : "body/m"} weight="bold">
          {text}
        </Text>
      </div>
    </div>
  );
};

export default StartGoalSpeechBubble;
